import { Link } from 'react-router-dom'
import { useAssessment } from '../../../contexts/AssessmentContext'
import {
  BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, Legend,
  ResponsiveContainer, PieChart, Pie, Cell
} from 'recharts'
import regulatoryUpdates from '../../../shared/data/regulatoryUpdates.json'
import { MATURITY_LEVELS, getMaturityLevelByScore } from '../../../shared/utils/maturityLevels'

const COLORS = ['#8AD0D6', '#5BA3A9', '#3D7A80', '#2A5A5F', '#A8DFE4', '#6EC4CB']

const getScoreColor = (score) => getMaturityLevelByScore(score).color

const CATEGORY_LABELS = {
  breach_notification: 'Breach Notification Change',
  penalty: 'Penalty Update',
  framework: 'Framework Update',
  authority: 'Authority Change',
  timeline: 'Timeline Change',
  new_regulation: 'New Regulation'
}

function RegulatoryUpdatesWidget() {
  const cutoff = new Date(Date.now() - 60 * 24 * 60 * 60 * 1000).toISOString()
  const allFindings = (regulatoryUpdates?.findings || []).filter(f =>
    !f.resolved && (f.publishedAt || f.detectedAt) >= cutoff
  )

  if (allFindings.length === 0) return null

  // Sort by severity (high first) then by date
  const severityOrder = { high: 0, medium: 1, low: 2 }
  const sorted = [...allFindings].sort((a, b) => {
    if (severityOrder[a.severity] !== severityOrder[b.severity]) {
      return severityOrder[a.severity] - severityOrder[b.severity]
    }
    return new Date(b.detectedAt) - new Date(a.detectedAt)
  })
  const top3 = sorted.slice(0, 3)

  return (
    <div className="card p-6">
      <div className="flex items-center gap-2 mb-4">
        <svg className="w-5 h-5 text-teal-600 dark:text-teal-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 3v18m0-18l-6 2l-3 9a5.002 5.002 0 006.001 0L6 5M12 3l6 2l3 9a5.002 5.002 0 01-6.001 0L18 5M8 21h8" />
        </svg>
        <h3 className="text-lg font-semibold text-gray-900 dark:text-white">Regulatory Updates</h3>
        <span className="text-xs text-gray-500 dark:text-gray-400">
          ({allFindings.length} active)
        </span>
        <Link
          to="/regulatory-updates"
          className="ml-auto inline-flex items-center gap-1 text-sm font-medium text-teal-600 dark:text-teal-400 hover:text-teal-700 dark:hover:text-teal-300 transition-colors"
        >
          View All
          <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
          </svg>
        </Link>
      </div>
      <div className="space-y-3">
        {top3.map((finding, idx) => (
          <Link
            key={`${finding.id}-${idx}`}
            to="/regulatory-updates"
            className={`block border-l-4 rounded-r-lg p-3 bg-gray-50 dark:bg-gray-800/50 hover:bg-gray-100 dark:hover:bg-gray-700/50 transition-colors ${
              finding.severity === 'high' ? 'border-l-red-500'
                : finding.severity === 'medium' ? 'border-l-amber-500'
                : 'border-l-blue-500'
            }`}
          >
            <div className="flex items-center gap-2 mb-1">
              <span className={`inline-flex px-1.5 py-0.5 text-xs font-medium rounded ${
                finding.severity === 'high' ? 'bg-red-100 dark:bg-red-900/30 text-red-700 dark:text-red-400'
                  : finding.severity === 'medium' ? 'bg-amber-100 dark:bg-amber-900/30 text-amber-700 dark:text-amber-400'
                  : 'bg-blue-100 dark:bg-blue-900/30 text-blue-700 dark:text-blue-400'
              }`}>
                {finding.severity}
              </span>
              <span className="inline-flex px-1.5 py-0.5 text-xs font-medium rounded bg-gray-200 dark:bg-gray-700 text-gray-600 dark:text-gray-300">
                {CATEGORY_LABELS[finding.category] || finding.category}
              </span>
              <span className="text-xs text-gray-400 dark:text-gray-500 ml-auto">
                {finding.affectedProfile}
              </span>
            </div>
            <p className="text-sm font-medium text-gray-900 dark:text-white">{finding.title}</p>
          </Link>
        ))}
      </div>
    </div>
  )
}

function Dashboard() {
  const { clients } = useAssessment()

  // Gather all completed assessments
  const completedAssessments = clients.flatMap(client =>
    (client.assessments || [])
      .filter(a => a.status === 'completed' && a.scores)
      .map(a => ({ ...a, clientName: client.name, clientId: client.id }))
  )

  // Calculate aggregate statistics
  const stats = {
    totalClients: clients.length,
    totalAssessments: completedAssessments.length,
    inProgressAssessments: clients.flatMap(c => c.assessments || []).filter(a => a.status === 'in_progress').length,
    averageMaturity: completedAssessments.length > 0
      ? Math.round(completedAssessments.reduce((sum, a) => sum + (a.scores?.overall?.score || 0), 0) / completedAssessments.length)
      : 0
  }

  // Category distribution across all assessments
  const categoryAverages = {}
  completedAssessments.forEach(assessment => {
    if (assessment.scores?.maturity?.categoryScores) {
      Object.entries(assessment.scores.maturity.categoryScores).forEach(([id, data]) => {
        if (!categoryAverages[data.name]) {
          categoryAverages[data.name] = { total: 0, count: 0 }
        }
        categoryAverages[data.name].total += data.score
        categoryAverages[data.name].count++
      })
    }
  })

  const categoryData = Object.entries(categoryAverages)
    .map(([name, data]) => ({
      name: name.length > 20 ? name.substring(0, 20) + '...' : name,
      fullName: name,
      score: Math.round(data.total / data.count)
    }))
    .sort((a, b) => b.score - a.score)

  // Maturity level distribution
  const maturityLevels = { 'Initial': 0, 'Developing': 0, 'Defined': 0, 'Managed': 0, 'Optimizing': 0 }
  completedAssessments.forEach(a => {
    const level = a.scores?.overall?.maturityLevel?.label
    if (level && maturityLevels.hasOwnProperty(level)) {
      maturityLevels[level]++
    }
  })

  const maturityDistribution = Object.entries(maturityLevels)
    .filter(([, count]) => count > 0)
    .map(([name, value]) => ({ name, value }))

  // Recent assessments
  const recentAssessments = completedAssessments
    .sort((a, b) => new Date(b.completedAt) - new Date(a.completedAt))
    .slice(0, 5)

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex justify-between items-center">
        <div>
          <h1 className="text-2xl font-bold text-gray-900 dark:text-white">Dashboard</h1>
          <p className="text-gray-600 dark:text-gray-400 mt-1">Overview of all assessments and trends</p>
        </div>
        <Link to="/clients" className="btn-primary">
          View All Clients
        </Link>
      </div>

      {/* Stats Cards */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
        <div className="card p-6">
          <div className="text-sm text-gray-500 dark:text-gray-400 mb-1">Total Clients</div>
          <div className="text-3xl font-bold text-gray-900 dark:text-white">{stats.totalClients}</div>
        </div>
        <div className="card p-6">
          <div className="text-sm text-gray-500 dark:text-gray-400 mb-1">Completed Assessments</div>
          <div className="text-3xl font-bold text-green-600 dark:text-green-400">{stats.totalAssessments}</div>
        </div>
        <div className="card p-6">
          <div className="text-sm text-gray-500 dark:text-gray-400 mb-1">In Progress</div>
          <div className="text-3xl font-bold text-yellow-600 dark:text-yellow-400">{stats.inProgressAssessments}</div>
        </div>
        <div className="card p-6">
          <div className="text-sm text-gray-500 dark:text-gray-400 mb-1">Avg. Maturity Score</div>
          <div className="text-3xl font-bold text-primary-600">{stats.averageMaturity}%</div>
        </div>
      </div>

      {/* Regulatory Updates */}
      <RegulatoryUpdatesWidget />

      {completedAssessments.length === 0 ? (
        <div className="card p-12 text-center">
          <svg className="w-16 h-16 text-gray-400 mx-auto mb-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 19v-6a2 2 0 00-2-2H5a2 2 0 00-2 2v6a2 2 0 002 2h2a2 2 0 002-2zm0 0V9a2 2 0 012-2h2a2 2 0 012 2v10m-6 0a2 2 0 002 2h2a2 2 0 002-2m0 0V5a2 2 0 012-2h2a2 2 0 012 2v14a2 2 0 01-2 2h-2a2 2 0 01-2-2z" />
          </svg>
          <h3 className="text-lg font-medium text-gray-900 dark:text-white mb-2">No completed assessments yet</h3>
          <p className="text-gray-600 dark:text-gray-400 mb-4">Complete your first assessment to see analytics and trends.</p>
          <Link to="/clients" className="btn-primary">
            Get Started
          </Link>
        </div>
      ) : (
        <>
          {/* Charts Row */}
          <div className="grid md:grid-cols-2 gap-6">
            {/* Category Scores Chart */}
            <div className="card p-6">
              <h3 className="text-lg font-semibold text-gray-900 dark:text-white mb-4">Average Scores by Category</h3>
              {(() => {
                const chartData = categoryData.slice(0, 8)
                return (
                  <>
                    <ResponsiveContainer width="100%" height={300}>
                      <BarChart data={chartData} layout="vertical">
                        <CartesianGrid strokeDasharray="3 3" stroke="rgba(63, 90, 106, 0.2)" />
                        <XAxis type="number" domain={[0, 100]} tick={{ fill: '#7E8F9C', fontSize: 11 }} />
                        <YAxis dataKey="name" type="category" width={150} tick={{ fill: '#7E8F9C', fontSize: 11 }} />
                        <Tooltip
                          formatter={(value, name, props) => [`${value}%`, props.payload.fullName]}
                          contentStyle={{ backgroundColor: '#012438', border: '1px solid #3F5A6A', borderRadius: '8px', color: '#BCC5CD' }}
                          itemStyle={{ color: '#8AD0D6' }}
                          labelStyle={{ color: '#BCC5CD' }}
                        />
                        <Bar dataKey="score" radius={[0, 4, 4, 0]}>
                          {chartData.map((entry, index) => (
                            <Cell key={`cat-${index}`} fill={getScoreColor(entry.score)} />
                          ))}
                        </Bar>
                      </BarChart>
                    </ResponsiveContainer>
                    <div className="flex items-center justify-center gap-3 mt-2 text-[10px] text-gray-500 dark:text-gray-400 flex-wrap">
                      {MATURITY_LEVELS.map((lv) => (
                        <span key={lv.level} className="flex items-center gap-1.5">
                          <span className="w-2.5 h-2.5 rounded-sm" style={{ backgroundColor: lv.color }} />
                          {lv.label} ({lv.range})
                        </span>
                      ))}
                    </div>
                  </>
                )
              })()}
            </div>

            {/* Maturity Distribution Pie Chart */}
            <div className="card p-6">
              <h3 className="text-lg font-semibold text-gray-900 dark:text-white mb-4">Maturity Level Distribution</h3>
              {maturityDistribution.length > 0 ? (
                <ResponsiveContainer width="100%" height={300}>
                  <PieChart>
                    <Pie
                      data={maturityDistribution}
                      cx="50%"
                      cy="50%"
                      labelLine={false}
                      label={({ name, percent }) => `${name} (${(percent * 100).toFixed(0)}%)`}
                      outerRadius={100}
                      fill="#8AD0D6"
                      dataKey="value"
                    >
                      {maturityDistribution.map((entry, index) => {
                        const lv = MATURITY_LEVELS.find((l) => l.label === entry.name)
                        return <Cell key={`cell-${index}`} fill={lv?.color || COLORS[index % COLORS.length]} />
                      })}
                    </Pie>
                    <Tooltip
                      contentStyle={{ backgroundColor: '#012438', border: '1px solid #3F5A6A', borderRadius: '8px', color: '#BCC5CD' }}
                      itemStyle={{ color: '#8AD0D6' }}
                    />
                    <Legend wrapperStyle={{ color: '#7E8F9C' }} />
                  </PieChart>
                </ResponsiveContainer>
              ) : (
                <div className="h-[300px] flex items-center justify-center text-gray-500 dark:text-gray-400">
                  Not enough data
                </div>
              )}
            </div>
          </div>

          {/* Recent Assessments */}
          <div className="card p-6">
            <h3 className="text-lg font-semibold text-gray-900 dark:text-white mb-4">Recent Assessments</h3>
            <div className="overflow-x-auto">
              <table className="min-w-full divide-y divide-gray-200 dark:divide-gray-700">
                <thead>
                  <tr>
                    <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">
                      Client
                    </th>
                    <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">
                      Date
                    </th>
                    <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">
                      Maturity Score
                    </th>
                    <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">
                      Level
                    </th>
                    <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">
                      Actions
                    </th>
                  </tr>
                </thead>
                <tbody className="bg-white dark:bg-gray-800 divide-y divide-gray-200 dark:divide-gray-700">
                  {recentAssessments.map(assessment => (
                    <tr key={assessment.id} className="hover:bg-gray-50 dark:hover:bg-gray-700">
                      <td className="px-4 py-4 whitespace-nowrap">
                        <span className="text-sm font-medium text-gray-900 dark:text-white">{assessment.clientName}</span>
                      </td>
                      <td className="px-4 py-4 whitespace-nowrap">
                        <span className="text-sm text-gray-500 dark:text-gray-400">
                          {new Date(assessment.completedAt).toLocaleDateString()}
                        </span>
                      </td>
                      <td className="px-4 py-4 whitespace-nowrap">
                        <div className="flex items-center">
                          <div className="w-16 bg-gray-200 dark:bg-gray-600 rounded-full h-2 mr-2">
                            <div
                              className="h-2 rounded-full"
                              style={{
                                width: `${assessment.scores?.overall?.score || 0}%`,
                                backgroundColor: getMaturityLevelByScore(assessment.scores?.overall?.score || 0).color
                              }}
                            />
                          </div>
                          <span className="text-sm font-medium text-gray-900 dark:text-white">
                            {assessment.scores?.overall?.score || 0}%
                          </span>
                        </div>
                      </td>
                      <td className="px-4 py-4 whitespace-nowrap">
                        {(() => {
                          const lv = assessment.scores?.overall?.maturityLevel
                          if (!lv?.label) {
                            return <span className="inline-flex px-2 py-1 text-xs font-medium rounded-full bg-gray-100 dark:bg-gray-700 text-gray-700 dark:text-gray-300">Unknown</span>
                          }
                          return (
                            <span className="inline-flex px-2 py-1 text-xs font-medium rounded-full" style={{ backgroundColor: `${lv.color}1F`, color: lv.color }}>
                              {lv.label}
                            </span>
                          )
                        })()}
                      </td>
                      <td className="px-4 py-4 whitespace-nowrap">
                        <Link
                          to={`/clients/${assessment.client_id || assessment.clientId}?section=program-overview`}
                          className="text-primary-600 hover:text-primary-800 dark:text-accent-300 dark:hover:text-accent-200 text-sm font-medium"
                        >
                          View Results
                        </Link>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        </>
      )}
    </div>
  )
}

export default Dashboard
